import { boundsExtentMeters, boundsOf, offsetLatLng, type LatLng } from './GeoMath';
import { frameToImage, frameToSite, type MapFrame } from './MapFrame';

/**
 * Everything about a site that is *known* rather than read off a photograph.
 *
 * ## The shape of the bet
 *
 * A vision model looking at an aerial frame is guessing at two things that are, for a great many
 * sites, matters of public record: **where the property line runs** and **what the ground does**.
 * Both are published — county and municipal parcel layers, USGS 3DEP and its equivalents elsewhere,
 * LiDAR-derived DEMs from state programmes — and every one of them is a network call away.
 *
 * None of that is wired up yet, and this module is deliberately built as though it were. The value
 * of doing it now is that it settles the two questions that get expensive to change later:
 *
 * - **What shape does the answer take?** A boundary is a ring of `LatLng`; a topography reading is a
 *   regular grid with a cell size and a datum. Both are georeferenced, and {@link MapFrame} converts
 *   either into site metres — so a source added in six months needs no new conversion, no new
 *   overlay code, and no second opinion about which way `+Z` points.
 * - **How does a consumer know what it is looking at?** Every result carries its {@link DataSource},
 *   and a source declares whether it is real, simulated, or not yet built. A simulated parcel that
 *   cannot be told apart from a surveyed one is worse than no parcel at all, so the distinction is
 *   in the data rather than in a comment.
 *
 * ## What is real today
 *
 * The elevation **file import** is real and does the whole job: an Esri ASCII grid or an XYZ export
 * from any DEM source becomes control points the terrain stage would otherwise have guessed at
 * (`ElevationImport.ts`). The parcel boundary is **simulated** — deterministic from the coordinates,
 * plausible in shape, and labelled as fiction everywhere it appears. The public endpoints are listed
 * with what each would take to wire, so the next person starts from a decision rather than a survey.
 *
 * Pure data and pure functions. Nothing here touches the network — a real source, when it arrives,
 * implements {@link SiteDataFetcher} beside its descriptor.
 */

export type DataSourceKind = 'parcel' | 'elevation';

/**
 * How much a result can be trusted.
 *
 * `simulated` is not a lesser grade of `available` — it is a different thing entirely, and the word
 * appears in the UI wherever the data does.
 */
export type DataSourceStatus = 'available' | 'simulated' | 'planned';

export interface DataSource {
  id: string;
  name: string;
  kind: DataSourceKind;
  status: DataSourceStatus;
  coverage: 'global' | 'united-states' | 'file';
  /** One line for the picker. */
  summary: string;
  /**
   * For a `planned` source: what wiring it actually takes. Kept as data so the next person picks up
   * a decision rather than a research task.
   */
  wiringNote?: string;
  attribution?: string;
}

export const SITE_DATA_SOURCES: readonly DataSource[] = [
  {
    id: 'parcel-simulated',
    name: 'Simulated parcel',
    kind: 'parcel',
    status: 'simulated',
    coverage: 'global',
    summary:
      'A plausible lot outline invented from the frame’s coordinates. Deterministic — the same place ' +
      'always gives the same shape — and never a real property line.',
  },
  {
    id: 'parcel-file',
    name: 'Boundary file',
    kind: 'parcel',
    status: 'available',
    coverage: 'file',
    summary: 'A GeoJSON polygon from a survey, a county download, or anything that exports one.',
  },
  {
    id: 'parcel-regrid',
    name: 'Regrid parcels',
    kind: 'parcel',
    status: 'planned',
    coverage: 'united-states',
    summary: 'Nationwide US parcel geometry with owner and zoning attributes.',
    wiringNote:
      'Commercial API, key per seat. Point query by lat/lng returns the parcel ring directly, so ' +
      'the work is a key field and a `SiteDataFetcher` — the geometry needs no conversion this ' +
      'module does not already do.',
  },
  {
    id: 'parcel-arcgis',
    name: 'County parcel layer (ArcGIS)',
    kind: 'parcel',
    status: 'planned',
    coverage: 'united-states',
    summary: 'Most US counties publish parcels as a public ArcGIS FeatureServer. Free, no key.',
    wiringNote:
      'The blocker is discovery, not fetching: every county has its own service URL and its own ' +
      'field names. Needs a small registry keyed by county FIPS, plus a `geometry=&geometryType=` ' +
      'point query returning GeoJSON — which `parseBoundaryGeoJson` already reads.',
  },
  {
    id: 'elevation-file',
    name: 'Topography file',
    kind: 'elevation',
    status: 'available',
    coverage: 'file',
    summary:
      'An Esri ASCII grid (.asc), an XYZ point export, or a GeoJSON of spot heights — what every ' +
      'DEM portal and most surveyors can produce.',
  },
  {
    id: 'elevation-3dep',
    name: 'USGS 3DEP elevation',
    kind: 'elevation',
    status: 'planned',
    coverage: 'united-states',
    summary: 'Public-domain 1 m LiDAR-derived DEM across most of the US.',
    wiringNote:
      'An ArcGIS ImageServer `exportImage` against the frame bbox in `f=json` / `lerc` form. The ' +
      'honest version reads the float raster; a cheap first version samples the point service on a ' +
      'grid instead, which reuses the same `ElevationGrid` this module already defines.',
  },
  {
    id: 'elevation-opentopo',
    name: 'OpenTopography global DEM',
    kind: 'elevation',
    status: 'planned',
    coverage: 'global',
    summary: 'SRTM and Copernicus DEMs worldwide, at 30 m — landform, not garden grading.',
    wiringNote:
      'Free API key, one GET returning a GeoTIFF or ASCII grid for a bbox. `parseAsciiGrid` reads ' +
      'the latter today. Worth flagging in the UI that 30 m cells describe the hill a site is on, ' +
      'not the fall across its lawn.',
  },
];

export function siteDataSource(id: string): DataSource | undefined {
  return SITE_DATA_SOURCES.find((source) => source.id === id);
}

export function sourcesOfKind(kind: DataSourceKind): DataSource[] {
  return SITE_DATA_SOURCES.filter((source) => source.kind === kind);
}

/* --------------------------------- Boundaries --------------------------------- */

export interface ParcelBoundary {
  /** Closed ring, first vertex not repeated at the end. */
  ring: LatLng[];
  sourceId: string;
  /** Whatever the source calls this parcel — an address, a lot number, a file name. */
  label: string;
  areaSquareMeters: number;
  /** True when nothing about this outline came from a record. */
  simulated: boolean;
}

/**
 * A parcel that is not a parcel.
 *
 * Deterministic from the coordinates, so revisiting a place gives the same lot back and a screenshot
 * of it stays true. Shaped like a real suburban plot — a street frontage, a deeper than wide body,
 * corners that are not quite square — because a simulated boundary whose only tell is that it is a
 * perfect rectangle teaches the wrong thing about what the real one will look like.
 *
 * It is labelled simulated in the type, in the UI, and in the prompt. It must never be the reason a
 * site's extent is set.
 */
export function simulateParcel(frame: MapFrame): ParcelBoundary {
  const seed = hashCoordinates(frame.center);
  const random = mulberry32(seed);

  // A lot that fills most of the frame but leaves the street and the neighbours visible.
  const width = frame.widthMeters * (0.5 + random() * 0.16);
  const depth = frame.lengthMeters * (0.58 + random() * 0.2);
  const jitter = Math.min(width, depth) * 0.06;

  const corners: [number, number][] = [
    [-width / 2, -depth / 2],
    [width / 2, -depth / 2],
    [width / 2, depth / 2],
    [-width / 2, depth / 2],
  ];

  const ring = corners.map(([east, south]) => {
    // The two street-side corners stay closer to true, because a frontage is surveyed off a road
    // centreline and is the straightest edge of a real lot.
    const slack = south < 0 ? jitter * 0.35 : jitter;
    return offsetLatLng(
      frame.center,
      east + (random() - 0.5) * slack * 2,
      south + (random() - 0.5) * slack * 2,
    );
  });

  return {
    ring,
    sourceId: 'parcel-simulated',
    label: 'Simulated lot',
    areaSquareMeters: ringAreaSquareMeters(frame, ring),
    simulated: true,
  };
}

/** The ring in the frame's normalized image space, ready for an overlay. */
export function boundaryToImage(frame: MapFrame, boundary: ParcelBoundary): [number, number][] {
  return boundary.ring.map((point) => frameToImage(frame, point));
}

/** The ring in world metres — the space the pipeline's own geometry lives in. */
export function boundaryToSite(frame: MapFrame, boundary: ParcelBoundary): [number, number][] {
  return boundary.ring.map((point) => {
    const site = frameToSite(frame, point);
    return [round2(site.x), round2(site.z)];
  });
}

/** Shoelace area, computed in site metres so the answer is in real square metres. */
export function ringAreaSquareMeters(frame: MapFrame, ring: readonly LatLng[]): number {
  const points = ring.map((point) => frameToSite(frame, point));
  let sum = 0;
  for (let i = 0; i < points.length; i += 1) {
    const a = points[i] as { x: number; z: number };
    const b = points[(i + 1) % points.length] as { x: number; z: number };
    sum += a.x * b.z - b.x * a.z;
  }
  return Math.abs(sum) / 2;
}

/** Bounds of a boundary, for "fit the frame to the lot". */
export function boundaryBounds(boundary: ParcelBoundary): ReturnType<typeof boundsOf> {
  return boundsOf(boundary.ring);
}

/** `0.18 acres · 740 m²` — both, because plots are discussed in both. */
export function describeArea(squareMeters: number): string {
  const acres = squareMeters / 4046.8564224;
  return `${Math.round(squareMeters).toLocaleString()} m²  ·  ${acres.toFixed(2)} acres`;
}

/* --------------------------------- Topography --------------------------------- */

/**
 * A regular grid of ground heights.
 *
 * Row-major from the **north-west** corner, rows running south — the same order an Esri ASCII grid
 * writes and the same order an image is scanned, so no source has to be flipped on the way in and
 * nothing downstream has to remember which way this one goes.
 */
export interface ElevationGrid {
  /** Centre of the north-west cell. */
  origin: LatLng;
  cellSizeMeters: number;
  cols: number;
  rows: number;
  /** Metres above the source's datum. `NaN` marks a cell the source had no reading for. */
  heights: Float32Array;
  sourceId: string;
  label: string;
  /** What the source said its vertical datum was, when it said anything. */
  datum?: string;
}

export function gridHeight(grid: ElevationGrid, col: number, row: number): number {
  if (col < 0 || row < 0 || col >= grid.cols || row >= grid.rows) return Number.NaN;
  return grid.heights[row * grid.cols + col] ?? Number.NaN;
}

/** Where a cell sits on Earth. */
export function gridCellLatLng(grid: ElevationGrid, col: number, row: number): LatLng {
  return offsetLatLng(grid.origin, col * grid.cellSizeMeters, row * grid.cellSizeMeters);
}

export interface GridStatistics {
  min: number;
  max: number;
  /** The median, used as the datum — a mean is dragged around by one bad cell, a median is not. */
  median: number;
  /** Cells with a reading. */
  sampled: number;
  /** `max - min` over the sampled cells. */
  reliefMeters: number;
}

export function gridStatistics(grid: ElevationGrid): GridStatistics {
  const values: number[] = [];
  for (const height of grid.heights) {
    if (Number.isFinite(height)) values.push(height);
  }
  if (values.length === 0) {
    return { min: 0, max: 0, median: 0, sampled: 0, reliefMeters: 0 };
  }
  values.sort((a, b) => a - b);
  const min = values[0] as number;
  const max = values[values.length - 1] as number;
  return {
    min,
    max,
    median: values[Math.floor(values.length / 2)] as number,
    sampled: values.length,
    reliefMeters: max - min,
  };
}

/** How well a grid actually covers a frame — the check before offering to use it. */
export function gridCoverage(
  grid: ElevationGrid,
  frame: MapFrame,
): { fraction: number; cellsInFrame: number; resolutionRatio: number } {
  let inside = 0;
  let sampled = 0;
  for (let row = 0; row < grid.rows; row += 1) {
    for (let col = 0; col < grid.cols; col += 1) {
      const height = gridHeight(grid, col, row);
      if (!Number.isFinite(height)) continue;
      sampled += 1;
      const [u, v] = frameToImage(frame, gridCellLatLng(grid, col, row));
      if (u >= 0 && u <= 1 && v >= 0 && v <= 1) inside += 1;
    }
  }
  return {
    fraction: sampled === 0 ? 0 : inside / sampled,
    cellsInFrame: inside,
    // Below ~1 the grid resolves detail inside the site; far above it, it only describes the hill
    // the site sits on. A 30 m global DEM on a 40 m garden lands around 0.75 and should say so.
    resolutionRatio: grid.cellSizeMeters / Math.max(1, frame.widthMeters / 20),
  };
}

/** One line describing an imported grid, for the panel. */
export function describeGrid(grid: ElevationGrid): string {
  const stats = gridStatistics(grid);
  return (
    `${grid.cols} × ${grid.rows} cells at ${grid.cellSizeMeters.toFixed(1)} m  ·  ` +
    `${stats.reliefMeters.toFixed(1)} m of relief across ${stats.sampled.toLocaleString()} readings`
  );
}

/* ---------------------------------- Helpers ---------------------------------- */

/** A stable 32-bit seed from a coordinate, rounded so a metre of pan does not reshape the lot. */
function hashCoordinates(point: LatLng): number {
  const key = `${point.lat.toFixed(4)},${point.lng.toFixed(4)}`;
  let hash = 2166136261;
  for (let i = 0; i < key.length; i += 1) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** The same small PRNG the site compiler uses, so "deterministic" means the same thing everywhere. */
function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

/** Re-exported so a consumer needs one import for "measure this rectangle". */
export { boundsExtentMeters };
